import { useReducer } from "react";
import StorageUnitContext, { internalState } from "./context";
import reducer from "./reducer";
import { execute } from "../../../utils/helpers/execute";
import EndPoints from "../../../services/end-points";
import { IStorageUnit, IStorageUnitDetails } from "../../../models/wms/storage-unit/response";
import { ICreateStorageUnit, IUpdateStorageUnit } from "../../../models/wms/storage-unit/request";
import { IStorageUnitQuery } from "../../../models/wms/storage-unit/query";

export interface IProps {
  children: React.ReactNode;
}

const StorageUnitContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
        const { data } = await EndPoints.wmsLayout.getAllStorageUnits(state.query);
        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDetails = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
        const { data } = await EndPoints.wmsLayout.getStorageUnit(id);
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
      },
      throwException: false,
    });
  };

  const setDetails = async (data?: IStorageUnitDetails) => {
    dispatch({ type: "SET_DETAILS", payload: { details: data } });
  };

  const createStorageUnit = async (request: ICreateStorageUnit) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });
        await EndPoints.wmsLayout.createStorageUnit(request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });
      },
      throwException: true,
    });
  };

  const updateStorageUnit = async (id: number, request: IUpdateStorageUnit) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.updateStorageUnit(id, request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const deleteStorageUnit = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
        await EndPoints.wmsLayout.deleteStorageUnit(id);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
      },
      throwException: true,
    });
  };

  const reorderStorageUnit = async (id: number, pickSequence: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.reorderStorageUnit(id, pickSequence);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IStorageUnitQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  return (
    <StorageUnitContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getDetails,
          setDetails,
          createStorageUnit,
          updateStorageUnit,
          deleteStorageUnit,
          reorderStorageUnit,
          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </StorageUnitContext.Provider>
  );
};

export default StorageUnitContextProvider;
